import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { AppProvider } from "@/contexts/AppContext";
import { AuthProvider } from "@/contexts/AuthContext";

// Main travel site
import Index from "./pages/Index";
import AlbanianTravel from "./pages/AlbanianTravel";
import LuxuryTravel from "./pages/LuxuryTravel";
import CorporateTravel from "./pages/CorporateTravel";
import YachtCharters from "./pages/YachtCharters";
import VillaRentals from "./pages/VillaRentals";
import TravelBlog from "./pages/TravelBlog";
import Booking from "./pages/Booking";
import Profile from "./pages/Profile";
import ResetPassword from "./pages/ResetPassword";
import InstagramContent from "./pages/InstagramContent";

// Admin / backend
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ContentPlanner from "./pages/admin/ContentPlanner";
import AIToolsDashboard from "./pages/AIToolsDashboard";
import MobileApp from "./pages/MobileApp";

import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

/**
 * Zera Travel — full site routing.
 *
 * Public site lives at `/` and friends, while the operational backend tools
 * (admin CRM, content planner, AI Social Tools dashboard) live under the
 * admin namespace and are linked from the Admin Dashboard.
 */
const App = () => (
  <ThemeProvider defaultTheme="light">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <AppProvider>
            <BrowserRouter>
              <Toaster />
              <Sonner />
              <Routes>
                {/* Public travel site */}
                <Route path="/" element={<Index />} />
                <Route path="/albanian-travel" element={<AlbanianTravel />} />
                <Route path="/luxury-travel" element={<LuxuryTravel />} />
                <Route path="/corporate-travel" element={<CorporateTravel />} />
                <Route path="/yacht-charters" element={<YachtCharters />} />
                <Route path="/villa-rentals" element={<VillaRentals />} />
                <Route path="/blog" element={<TravelBlog />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/instagram-content" element={<InstagramContent />} />

                {/* Backend / admin — all live under /mtnadmin */}
                <Route path="/mtnadmin" element={<AdminLogin />} />
                <Route path="/mtnadmin/dashboard" element={<AdminDashboard />} />
                <Route path="/mtnadmin/instagram" element={<AdminDashboard />} />
                <Route path="/mtnadmin/brand-studio" element={<AdminDashboard />} />
                <Route path="/mtnadmin/content-planner" element={<ContentPlanner />} />
                <Route path="/mtnadmin/ai-tools" element={<AIToolsDashboard />} />
                {/* Legacy /admin aliases (kept so existing links keep working) */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/instagram" element={<AdminDashboard />} />
                <Route path="/admin/brand-studio" element={<AdminDashboard />} />
                <Route path="/admin/content-planner" element={<ContentPlanner />} />
                <Route path="/admin/ai-tools" element={<AIToolsDashboard />} />

                <Route path="/ai-tools" element={<AIToolsDashboard />} />

                {/* Installable iOS/Android PWA — mirrors the backend 5-step workflow */}
                <Route path="/app" element={<MobileApp />} />




                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </AppProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
